import { QueryClient } from '@tanstack/react-query';




import { PIXEL_DESTINATIONS_QUERY_KEY } from '@/lib/query/constants/pixel-destinations-query-key-constants';
import { USER_ACCOUNTS_QUERY_KEY } from '@/lib/query/constants/account-keys-constants';
import { SUBSCRIPTION_DEPENDENT_QUERY_KEYS } from '@/lib/query/constants/subscription-keys-constants';
import type {
  InvalidateOptions,
} from './types/cache-invalidation-types';
import { CACHE_DEPENDENCIES } from './constants/cache-invalidation-constants';

const ACCOUNT_SCOPED_QUERY_PREFIXES = [
  // Destinations
  PIXEL_DESTINATIONS_QUERY_KEY,
  'PIXEL_EVENT_CONFIG',
  'CONDITION_FIELDS_CATALOG',
  'customers',

  // Dashboard widgets and measures
  'widgetMeasureQuery',
  'measure',
  'attributionData',
  'cohortAnalysis',

  // EMQ (Event Match Quality) queries
  'emq',
  'emqSummary',
  'emqTimeSeries',
  'emqDiagnostics',
  'emqOverview',
  'emqDatasets',
  'emqDatasetOverview',
  'emqEventDetail',
  'emqMatchKeys',
  'emqDedupeKeys',

  // Settings and preferences
  'alert-preferences',

  // Shopify data
  'SHOPIFY_COLLECTIONS',
  'SHOPIFY_TAGS',

  // Team and account data
  'teamMembers',

  // Onboarding and health checks
  'firstEventDetection',
  'firstEventDetection-initial',
  'orderSources',
  'pageViewLastSeen',
  'purchaseEventHealth',
  'destinationActivity',
] as const;

export function invalidateRelatedQueries(queryClient: QueryClient, queryKey: string, options?: InvalidateOptions) {
  const relatedKeys = CACHE_DEPENDENCIES[queryKey] || [queryKey];
  relatedKeys.forEach((key: string) =>
    void queryClient.invalidateQueries({ queryKey: [key], refetchType: options?.refetchType })
  );
}

export function invalidateUserData(queryClient: QueryClient) {
  invalidateRelatedQueries(queryClient, USER_ACCOUNTS_QUERY_KEY);
}

export function invalidateSubscriptionData(queryClient: QueryClient) {
  invalidateUserData(queryClient);
  SUBSCRIPTION_DEPENDENT_QUERY_KEYS.forEach(key =>
    void queryClient.invalidateQueries({ queryKey: [key] })
  );
}

export function invalidateReportingQueries(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ['measure'] });
  void queryClient.invalidateQueries({ queryKey: ['widgetMeasureQuery'] });
  void queryClient.invalidateQueries({
    predicate: query =>
      Array.isArray(query.queryKey) && query.queryKey.includes('attributionData'),
  });
}

export function invalidateDestinationData(queryClient: QueryClient, options?: InvalidateOptions) {
  invalidateRelatedQueries(queryClient, 'DESTINATIONS', options);
}

export function removeDestinationQueries(queryClient: QueryClient) {
  queryClient.removeQueries({ queryKey: [PIXEL_DESTINATIONS_QUERY_KEY] });
}

export function clearAccountScopedQueries(queryClient: QueryClient) {
  ACCOUNT_SCOPED_QUERY_PREFIXES.forEach(prefix => {
    queryClient.removeQueries({
      predicate: query => {
        const key = query.queryKey;
        if (Array.isArray(key) && key.length > 0) {
          // Check if prefix appears anywhere in the key, not just at index 0.
          // Attribution keys have structure [pixelId, 'attributionData', ...]
          // so 'attributionData' is at index 1, not 0.
          return key.includes(prefix);
        }
        return false;
      },
    });
  });
}
